import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Typography,
  Modal,
  Backdrop,
  Fade,
  InputBase,
  IconButton,
  Box,
  useTheme,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  OndemandVideo as OndemandVideoIcon,
  Clear as ClearIcon,
} from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

// ─── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  // ── Page wrapper ─────────────────────────────────────────────────────
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      overflowY: "auto",
    },
  },

  // ── Hero / header ─────────────────────────────────────────────────────
  hero: {
    flexShrink: 0,
    padding: theme.spacing(3, 0, 2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2.5),
  },
  heroTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: "clamp(1.35rem, 2.5vw, 1.75rem)",
    color: theme.palette.text.primary,
    lineHeight: 1.25,
    letterSpacing: "-0.025em",
    marginBottom: theme.spacing(0.5),
  },
  heroSubtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("xs")]: {
      whiteSpace: "normal",
    },
  },
  heroStats: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexShrink: 0,
    paddingTop: theme.spacing(0.5),
  },
  statChip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 1,
  },
  statNumber: {
    fontWeight: 800,
    fontSize: "1.5rem",
    lineHeight: 1,
    color: theme.palette.text.primary,
    letterSpacing: "-0.03em",
  },
  statLabel: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
  },

  // ── Search bar ────────────────────────────────────────────────────────
  searchWrap: {
    display: "flex",
    alignItems: "center",
    backgroundColor:
      theme.palette.type === "light"
        ? "#f4f6fb"
        : theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(0.5, 1.5),
    gap: theme.spacing(1),
    height: 40,
    transition: "border-color 0.15s, box-shadow 0.15s",
    maxWidth: 360,
    width: "100%",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${
        theme.palette.type === "light"
          ? "rgba(99,102,241,0.12)"
          : "rgba(99,102,241,0.2)"
      }`,
      backgroundColor: theme.palette.background.paper,
    },
  },
  searchIcon: {
    fontSize: 18,
    color: theme.palette.text.disabled,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    "& input::placeholder": {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
  searchClear: {
    padding: 2,
    color: theme.palette.text.disabled,
    "&:hover": { color: theme.palette.text.secondary },
  },

  // ── Scroll body ───────────────────────────────────────────────────────
  body: {
    flex: 1,
    overflowY: "auto",
    paddingBottom: theme.spacing(4),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      overflowY: "visible",
      flex: "none",
    },
  },

  // ── Results meta ──────────────────────────────────────────────────────
  resultsMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontWeight: 600,
    letterSpacing: "0.04em",
    marginBottom: theme.spacing(2),
  },
  highlight: {
    color: theme.palette.text.secondary,
  },

  // ── Cards grid ────────────────────────────────────────────────────────
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },

  // ── Card ──────────────────────────────────────────────────────────────
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow:
        theme.palette.type === "light"
          ? "0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)"
          : "0 12px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)",
      borderColor:
        theme.palette.type === "light"
          ? "rgba(99,102,241,0.35)"
          : "rgba(99,102,241,0.4)",
      "& $thumbOverlay": { opacity: 1 },
      "& $playBtn": { transform: "scale(1.08)" },
    },
    "&:active": {
      transform: "translateY(-1px)",
    },
  },

  // ── Thumbnail area ────────────────────────────────────────────────────
  thumbWrap: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
    backgroundColor:
      theme.palette.type === "light" ? "#e8eef6" : "#111827",
    overflow: "hidden",
  },
  thumb: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  thumbOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(99,102,241,0.55) 0%, rgba(16,16,40,0.72) 100%)",
    opacity: 0,
    transition: "opacity 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  playBtn: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.95)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
    transition: "transform 0.18s ease",
    "& svg": {
      fontSize: 26,
      color: "#4f46e5",
      marginLeft: 3,
    },
  },
  thumbPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: 40,
      color:
        theme.palette.type === "light"
          ? "rgba(0,0,0,0.12)"
          : "rgba(255,255,255,0.08)",
    },
  },

  // ── Card body ─────────────────────────────────────────────────────────
  cardBody: {
    padding: theme.spacing(1.75, 2, 2, 2),
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    letterSpacing: "-0.01em",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardDesc: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginTop: theme.spacing(0.25),
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: theme.spacing(1),
  },
  cardDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    flexShrink: 0,
  },
  cardLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.disabled,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },

  // ── Skeleton loading ──────────────────────────────────────────────────
  skeletonCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },
  skeletonThumb: {
    width: "100%",
    paddingTop: "56.25%",
    backgroundColor:
      theme.palette.type === "light" ? "#e8eef6" : "#1a2236",
    animation: "$shimmer 1.5s infinite",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        theme.palette.type === "light"
          ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)"
          : "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
      animation: "$shimmerSlide 1.5s infinite",
    },
  },
  skeletonBody: {
    padding: theme.spacing(1.75, 2, 2, 2),
  },
  skeletonLine: {
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === "light" ? "#e8eef6" : "#1a2236",
    animation: "$shimmer 1.5s infinite",
    marginBottom: theme.spacing(1),
  },
  "@keyframes shimmer": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.6 },
    "100%": { opacity: 1 },
  },
  "@keyframes shimmerSlide": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(100%)" },
  },

  // ── Empty state ───────────────────────────────────────────────────────
  emptyWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    textAlign: "center",
    gap: theme.spacing(1.5),
  },
  emptyIcon: {
    width: 72,
    height: 72,
    borderRadius: 20,
    backgroundColor:
      theme.palette.type === "light"
        ? "rgba(99,102,241,0.07)"
        : "rgba(99,102,241,0.12)",
    border: `1px solid ${
      theme.palette.type === "light"
        ? "rgba(99,102,241,0.12)"
        : "rgba(99,102,241,0.2)"
    }`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: 32,
      color: theme.palette.primary.main,
      opacity: 0.6,
    },
  },
  emptyTitle: {
    fontWeight: 700,
    fontSize: "1.05rem",
    color: theme.palette.text.primary,
    letterSpacing: "-0.01em",
  },
  emptySubtitle: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    maxWidth: 320,
    lineHeight: 1.55,
  },

  // ── Video Modal ───────────────────────────────────────────────────────
  modalBackdrop: {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0,0,0,0.72)",
  },
  modalWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    outline: "none",
  },
  modalBox: {
    outline: "none",
    width: "min(900px, 96vw)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    animation: "$fadeSlideUp 0.22s ease",
  },
  "@keyframes fadeSlideUp": {
    "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    paddingLeft: 2,
  },
  modalTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#fff",
    lineHeight: 1.35,
    flex: 1,
  },
  modalClose: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "background 0.15s",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
    "& svg": { fontSize: 18, color: "#fff" },
  },
  modalVideo: {
    width: "100%",
    aspectRatio: "16/9",
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: "#000",
    boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
}));

// ─── Skeleton card ─────────────────────────────────────────────────────────────

const SkeletonCard = ({ classes }) => (
  <div className={classes.skeletonCard}>
    <div className={classes.skeletonThumb} />
    <div className={classes.skeletonBody}>
      <div className={classes.skeletonLine} style={{ height: 14, width: "75%" }} />
      <div className={classes.skeletonLine} style={{ height: 12, width: "50%", opacity: 0.6 }} />
    </div>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────

const Helps = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const { list } = useHelps();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const helps = await list();
      setRecords(helps);
      setLoading(false);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = useCallback((video, title) => {
    setSelectedVideo(video);
    setSelectedTitle(title || "");
  }, []);

  const closeVideoModal = useCallback(() => {
    setSelectedVideo(null);
    setSelectedTitle("");
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") closeVideoModal();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [closeVideoModal]);

  const filtered = records.filter((r) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      (r.title || "").toLowerCase().includes(term) ||
      (r.description || "").toLowerCase().includes(term)
    );
  });

  const renderSkeletons = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <SkeletonCard key={i} classes={classes} />
    ));

  const renderEmpty = () => (
    <div className={classes.emptyWrap}>
      <div className={classes.emptyIcon}>
        <OndemandVideoIcon />
      </div>
      <Typography className={classes.emptyTitle}>
        {search
          ? "Nenhum resultado encontrado"
          : "Nenhum vídeo disponível"}
      </Typography>
      <Typography className={classes.emptySubtitle}>
        {search
          ? `Não encontramos nenhum vídeo para "${search}". Tente outros termos.`
          : "Os vídeos de ajuda aparecerão aqui quando forem adicionados."}
      </Typography>
    </div>
  );

  const renderCards = () =>
    filtered.map((record, idx) => (
      <div
        key={record.id || idx}
        className={classes.card}
        role="button"
        tabIndex={0}
        onClick={() => openVideoModal(record.video, record.title)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openVideoModal(record.video, record.title);
          }
        }}
      >
        <div className={classes.thumbWrap}>
          {record.video ? (
            <img
              src={`https://img.youtube.com/vi/${record.video}/mqdefault.jpg`}
              alt={record.title || "Video thumbnail"}
              className={classes.thumb}
              loading="lazy"
            />
          ) : (
            <div className={classes.thumbPlaceholder}>
              <OndemandVideoIcon />
            </div>
          )}
          <div className={classes.thumbOverlay}>
            <div className={classes.playBtn}>
              <PlayArrowIcon />
            </div>
          </div>
        </div>
        <div className={classes.cardBody}>
          <Typography className={classes.cardTitle}>
            {record.title || "Sem título"}
          </Typography>
          {record.description && (
            <Typography className={classes.cardDesc}>
              {record.description}
            </Typography>
          )}
          <div className={classes.cardFooter}>
            <span className={classes.cardDot} />
            <span className={classes.cardLabel}>Assistir no player</span>
          </div>
        </div>
      </div>
    ));

  return (
    <MainContainer>
      <div className={classes.page}>
        {/* ── Hero ── */}
        <div className={classes.hero}>
          <div className={classes.heroTop}>
            <div>
              <Typography className={classes.heroTitle}>
                {i18n.t("helps.title")}
              </Typography>
              <Typography className={classes.heroSubtitle}>
                Aprenda a usar a plataforma com nossos vídeos tutoriais.
                Clique em qualquer card para assistir.
              </Typography>
            </div>
            {!loading && records.length > 0 && (
              <div className={classes.heroStats}>
                <div className={classes.statChip}>
                  <span className={classes.statNumber}>{records.length}</span>
                  <span className={classes.statLabel}>
                    vídeo{records.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className={classes.searchWrap}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              placeholder="Buscar vídeos…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ "aria-label": "buscar vídeos" }}
            />
            {search && (
              <IconButton
                size="small"
                className={classes.searchClear}
                onClick={() => setSearch("")}
                aria-label="limpar busca"
              >
                <ClearIcon style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className={classes.body}>
          {!loading && filtered.length > 0 && search && (
            <Typography className={classes.resultsMeta}>
              <span className={classes.highlight}>{filtered.length}</span>{" "}
              resultado{filtered.length !== 1 ? "s" : ""} para &ldquo;{search}&rdquo;
            </Typography>
          )}

          {loading ? (
            <div className={classes.grid}>{renderSkeletons()}</div>
          ) : filtered.length === 0 ? (
            renderEmpty()
          ) : (
            <div className={classes.grid}>{renderCards()}</div>
          )}
        </div>
      </div>

      {/* ── Video Modal ── */}
      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ className: classes.modalBackdrop, timeout: 200 }}
      >
        <Fade in={Boolean(selectedVideo)}>
          <div className={classes.modalWrap} onClick={closeVideoModal}>
            <div
              className={classes.modalBox}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={classes.modalHeader}>
                <Typography className={classes.modalTitle} noWrap>
                  {selectedTitle || "Vídeo de ajuda"}
                </Typography>
                <div
                  className={classes.modalClose}
                  role="button"
                  tabIndex={0}
                  onClick={closeVideoModal}
                  onKeyDown={(e) => e.key === "Enter" && closeVideoModal()}
                  aria-label="fechar"
                >
                  <CloseIcon />
                </div>
              </div>
              <div className={classes.modalVideo}>
                {selectedVideo && (
                  <iframe
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1&rel=0`}
                    title={selectedTitle || "YouTube video player"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    </MainContainer>
  );
};

export default Helps;
